import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUploadUrl } from '../utils/apiUrl';
import { getContrastStyles } from '../utils/colorContrast';
import DistrictAdminsList from './districtadmin/DistrictAdminsList';
import AddDistrictAdmin from './districtadmin/AddDistrictAdmin';
import EditDistrictAdmin from './districtadmin/EditDistrictAdmin';
import ViewForms from './districtadmin/ViewForms';
import ViewStudents from './districtadmin/ViewStudents';
import EditStudentForm from './districtadmin/EditStudentForm';
import EditDistrictInfo from './districtadmin/EditDistrictInfo';
import RegistrationCodesList from './districtadmin/RegistrationCodesList';
import AddRegistrationCode from './districtadmin/AddRegistrationCode';
import EditRegistrationCode from './districtadmin/EditRegistrationCode';
import StudentMap from './districtadmin/StudentMap';
import Reports from './districtadmin/Reports';
import SchoolLunchSubmissions from './districtadmin/SchoolLunchSubmissions';
import RecentlyDeleted from './districtadmin/RecentlyDeleted';
import AuditLogs from './districtadmin/AuditLogs';
import ReturningStudentCSVUpload from './districtadmin/ReturningStudentCSVUpload';
import './Dashboard.css';
import './SuperAdminDashboard.css';

const DistrictAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const primaryColor = user?.primary_color || '#2c3e50';
  const logoUrl = user?.logo_url;
  const subdomain = user?.subdomain;

  // Get contrast-optimized styles for header
  const headerStyle = getContrastStyles(primaryColor);

  return (
    <div className="dashboard-container">
      <header 
        className="dashboard-header" 
        style={headerStyle}
      >
        <div className="dashboard-header-content">
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          {logoUrl && (
            <img 
              src={getUploadUrl(logoUrl, subdomain)} 
              alt={user?.district_name || 'District Logo'} 
              className="dashboard-logo"
              onError={(e) => {
                console.error('[Logo] Failed to load:', {
                  logoUrl,
                  subdomain,
                  fullUrl: getUploadUrl(logoUrl, subdomain),
                  error: 'Image load failed'
                });
                // Hide broken image
                e.target.style.display = 'none';
              }}
            />
          )}
          <h1>{user?.district_name || 'District'} Admin Dashboard</h1>
        </div>
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </header>
      {mobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
      )}
      <div className="dashboard-content">
        <nav className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <h2>Menu</h2>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <ul>
            <li>
              <Link to="/district-admin/admins" onClick={closeMobileMenu}>District Users</Link>
              <ul className="submenu">
                <li><Link to="/district-admin/admins/add" onClick={closeMobileMenu}>Add User</Link></li>
                <li><Link to="/district-admin/admins" onClick={closeMobileMenu}>List Users</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/district-admin/forms" onClick={closeMobileMenu}>Forms</Link>
            </li>
            <li>
              <Link to="/district-admin/students" onClick={closeMobileMenu}>Students</Link>
            </li>
            <li>
              <Link to="/district-admin/student-map" onClick={closeMobileMenu}>Student Map</Link>
            </li>
            <li>
              <Link to="/district-admin/registration-codes" onClick={closeMobileMenu}>Registration Codes</Link>
              <ul className="submenu">
                <li><Link to="/district-admin/registration-codes/add" onClick={closeMobileMenu}>Add Code</Link></li>
                <li><Link to="/district-admin/registration-codes" onClick={closeMobileMenu}>List Codes</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/district-admin/reports" onClick={closeMobileMenu}>Reports</Link>
              <ul className="submenu">
                <li><Link to="/district-admin/reports/csv" onClick={closeMobileMenu}>CSV Reports</Link></li>
                <li><Link to="/district-admin/reports/pdf" onClick={closeMobileMenu}>PDF Reports</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/district-admin/school-lunch" onClick={closeMobileMenu}>School Lunch Applications</Link>
            </li>
            <li>
              <Link to="/district-admin/settings" onClick={closeMobileMenu}>School Info</Link>
            </li>
            <li>
              <Link to="/district-admin/returning-student-csv" onClick={closeMobileMenu}>Returning Student CSV</Link>
            </li>
            <li>
              <Link to="/district-admin/recently-deleted" onClick={closeMobileMenu}>Recently Deleted</Link>
            </li>
            <li>
              <Link to="/district-admin/audit-logs" onClick={closeMobileMenu}>Audit Logs</Link>
            </li>
          </ul>
        </nav>
        <main className="dashboard-main">
          <Routes>
            <Route path="/" element={<div><h2>Welcome to {user?.district_name || 'District'} Admin Dashboard</h2></div>} />
            <Route path="/admins" element={<DistrictAdminsList />} />
            <Route path="/admins/add" element={<AddDistrictAdmin />} />
            <Route path="/admins/edit/:id" element={<EditDistrictAdmin />} />
            <Route path="/forms" element={<ViewForms />} />
            <Route path="/students" element={<ViewStudents />} />
            <Route path="/edit-student/:studentIntakeId" element={<EditStudentForm />} />
            <Route path="/student-map" element={<StudentMap />} />
            <Route path="/registration-codes" element={<RegistrationCodesList />} />
            <Route path="/registration-codes/add" element={<AddRegistrationCode />} />
            <Route path="/registration-codes/edit/:id" element={<EditRegistrationCode />} />
            <Route path="/reports/*" element={<Reports />} />
            <Route path="/school-lunch" element={<SchoolLunchSubmissions />} />
            <Route path="/settings" element={<EditDistrictInfo />} />
            <Route path="/returning-student-csv" element={<ReturningStudentCSVUpload />} />
            <Route path="/recently-deleted" element={<RecentlyDeleted />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default DistrictAdminDashboard;

